import {
  pipe,
  pathOr,
  isEmpty,
  identity,
  replace,
  toLower,
  isNil,
  pathSatisfies,
  split,
  map,
  sort,
  ascend,
  descend,
  join,
  path,
  prop,
  sortWith,
  groupWith,
  mergeAll,
  values,
  sum,
  equals,
  includes,
  propOr,
  either,
  unless,
  find,
  propEq,
  defaultTo,
  ifElse,
  always,
  pathEq,
  filter,
  length,
  allPass,
  not
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { stringFormatter } from '@telus/alloy-analytics'

import {
  getCartType,
  getFirstMonthAddons,
  getProductName as getProdName,
  getContractLength as getContractTerm
} from '../cart'
import { getBackorderedItems, getOutOfStockItems } from '../rti'
import { getBYODOffers } from '../../components/organisms/Byod/utils'
import { getMobilityOffers, isEsim, isMobilePhone } from '../mobility'

const { getCustomerType } = utils.customer
const {
  getMonthlyItemPrice,
  getItemOneTimePrice,
  getMonthlyAddons,
  getItemTotalFirstBill,
  isAffiliateFlow
} = utils.cart

/* Common - Start */
const formatString = (value) => {
  if (typeof value !== 'string') return ''
  return value
    ?.toLowerCase()
    ?.trim()
    ?.replace(/,/g, '')
    ?.replace(/\s/g, '_')
    ?.replace(/\//g, '-')
    ?.replace(/&/g, 'and')
}
/* Common - End */

/* Order Type - Start */
const getLobAndCustomerType = (cart) => {
  const customer = pathOr({}, ['root', 'customer'])(cart)
  const lineOfBusiness = getCartType(cart)
  const customerType = getCustomerType(customer)
  const lobAndCustomerTypeMap = {
    consumer: {
      mobility: 'mobility',
      hs: 'home solutions',
      mixed: 'mobility | home solutions'
    },
    business: {
      mobility: 'mobility | business',
      hs: 'home solutions | business',
      mixed: 'mobility | home solutions | business'
    },
    epp: {
      mobility: 'mobility | epp'
    }
  }

  const cartType = pathOr('', [customerType, lineOfBusiness])(lobAndCustomerTypeMap)
  return isAffiliateFlow(cart) ? `${cartType} | affiliate` : cartType
}

const buildCartType = (cart) => {
  if (isNil(cart)) return 'error'

  const isCartEmpty = pathSatisfies(isEmpty, ['root', 'items'])
  if (isCartEmpty(cart)) return 'empty cart'

  return getLobAndCustomerType(cart)
}

const getOrderType = pipe(
  buildCartType,
  split('|'),
  map(formatString),
  sort(ascend(identity)),
  join('|')
)
/* Order Type - End */

/* Products - Start */
const getHsAddOnItemPrice = (item, defaultResult, monthlyItemPrice) => {
  const isMonthlyAddon = getMonthlyAddons([item]).length > 0
  const isFirstMonthAddon = getFirstMonthAddons([item]).length > 0
  // financed and included addons are billed monthly
  if (isMonthlyAddon)
    return {
      price: monthlyItemPrice?.subTotal,
      taxes: monthlyItemPrice?.taxes
    }

  // one time payment addons are charged to the first bill
  if (isFirstMonthAddon) {
    const itemPrice = getItemTotalFirstBill(item)
    return {
      price: itemPrice?.subTotal,
      taxes: itemPrice?.taxes
    }
  }

  return defaultResult
}

const getHsCreditItemPrice = (item) => {
  const itemPrice = path(['totalPrice', 'recurring', 0, 'recurringCreditBreakdowns', 0], item)
  return {
    price: itemPrice?.credit,
    taxes: itemPrice?.taxes
  }
}

const getHsItemPrice = (item) => {
  const defaultResult = {
    price: 0,
    taxes: []
  }
  const { tags } = item
  const itemOneTimePrice = getItemOneTimePrice(item)
  const monthlyItemPrice = getMonthlyItemPrice(item)
  const isAddon = includes('addon')(tags)

  if (isAddon) return getHsAddOnItemPrice(item, defaultResult, monthlyItemPrice)

  if (item.category === 'HS_CREDIT') {
    return getHsCreditItemPrice(item)
  }

  if (item.category === 'HS_GIFT' || item.tags.includes('gift')) return defaultResult

  // all other HS products should have a monthly price
  // or the one-time price
  if (monthlyItemPrice)
    return {
      price: monthlyItemPrice?.subTotal,
      taxes: monthlyItemPrice?.taxes
    }

  return {
    price: itemOneTimePrice?.subTotal,
    taxes: itemOneTimePrice?.taxes
  }
}

const castItemPriceProps = (itemPrice) => {
  return {
    price: isNil(itemPrice?.price) ? 0 : itemPrice.price,
    taxes: isNil(itemPrice?.taxes) ? [] : itemPrice.taxes
  }
}

const getMobilityItemPrice = (item) => {
  const { category, easyPayment } = item
  const oneTimePrice = getItemOneTimePrice(item)
  const oneTimeSubTotal = propOr(0, 'subTotal')(oneTimePrice)
  const oneTimeCredit = propOr(0, 'credit')(oneTimePrice)
  const monthlyItemPrice = getMonthlyItemPrice(item)
  const monthlySubTotal = propOr(0, 'subTotal')(monthlyItemPrice)

  const mobilityItemPriceMap = {
    MOBILITY_ACCESSORY: oneTimeSubTotal,
    MOBILITY_PHONE: oneTimeSubTotal - oneTimeCredit,
    MOBILITY_PLAN: monthlySubTotal,
    MOBILITY_WARRANTY: monthlySubTotal,
    MOBILITY_CONNECTION_FEE: oneTimeSubTotal,
    MOBILITY_SIM: oneTimeSubTotal,
    MOBILITY_OFFER: easyPayment?.amount
  }

  const mobilityItemTaxesMap = {
    MOBILITY_ACCESSORY: oneTimePrice?.taxes,
    MOBILITY_PHONE: oneTimePrice?.taxes,
    MOBILITY_PLAN: monthlyItemPrice?.taxes,
    MOBILITY_WARRANTY: monthlyItemPrice?.taxes,
    MOBILITY_CONNECTION_FEE: oneTimePrice?.taxes,
    MOBILITY_SIM: oneTimePrice?.taxes,
    MOBILITY_OFFER: easyPayment?.taxes
  }

  const price = mobilityItemPriceMap[category]
  const taxes = mobilityItemTaxesMap[category]

  return { price, taxes }
}

const getProductPrice = (product) => {
  const { category } = product
  if (category.lastIndexOf('HS_', 0) === 0) return castItemPriceProps(getHsItemPrice(product)).price
  if (category.lastIndexOf('MOBILITY_', 0) === 0)
    return castItemPriceProps(getMobilityItemPrice(product)).price
  return 0
}

const getOfferOrderType = (product, cart) => {
  const customer = pathOr({}, ['root', 'customer'])(cart)
  const provisioner = propOr('', 'provisioner')(product)
  const productOrderType = pathOr('', ['productOrder', 'type'])(product)
  const customerType = getCustomerType(customer)

  const hsOrderTypesMap = {
    consumer: {
      ACQUISITION: 'hs_new',
      RECONTRACTING: 'hs_renew',
      UPSELL: 'hs_aal'
    },
    business: {
      ACQUISITION: 'bus_new',
      RECONTRACTING: 'bus_renew',
      UPSELL: 'bus_aal'
    }
  }

  const mobilityOrderTypesMap = {
    consumer: {
      ACTIVATION: 'mob_new',
      RENEWAL: 'mob_renew',
      AAL: 'mob_aal',
      OUTRIGHT_PURCHASE: 'mob_new'
    },
    business: {
      ACTIVATION: 'bus_new',
      RENEWAL: 'bus_renew',
      AAL: 'bus_aal',
      OUTRIGHT_PURCHASE: 'bus_new'
    },
    epp: {
      ACTIVATION: 'epp_new',
      RENEWAL: 'epp_renew',
      OUTRIGHT_PURCHASE: 'epp_new'
    }
  }

  if (['ESS', 'UPC'].includes(provisioner)) {
    return pathOr('', [customerType, productOrderType])(mobilityOrderTypesMap)
  }
  if (['FIFA', 'COMPASS'].includes(provisioner)) {
    return pathOr('', [customerType, productOrderType])(hsOrderTypesMap)
  }

  return ''
}

const getProductColour = pipe(
  either(
    path(['content', 'colour', 'fields', 'name']),
    pipe(
      path(['productOffering', 'characteristics']),
      unless(
        isNil,
        pipe(find(propEq('name', 'colour')), prop('displayValue'), unless(isNil, formatString))
      )
    )
  ),
  defaultTo('')
)

const getProductBrand = (product) => {
  return formatString(pathOr('', ['content', 'brand', 'fields', 'name'], product))
}

const getDeviceStorage = pipe(
  pathOr('', ['content', 'storage', 'fields', 'ref']),
  replace(/ /g, ''),
  toLower
)

const getServiceTier = ifElse(
  either(includes('channel'), includes('pack')),
  ifElse(includes('premium'), always('premium'), always('regular')),
  always('')
)

const getProductProvisioner = ifElse(
  pipe(path(['tags']), includes('internet')),
  prop('provisioner'),
  always('')
)

const getProductGenre = (item) => {
  if (!item.tags.includes('themepack')) return ''

  const formattedGenre = pipe(
    pathOr([], ['content', 'offerAttributes']),
    find(pathEq(['fields', 'type'], 'Genre')),
    pathOr('', ['fields', 'label']),
    formatString
  )(item)

  return formattedGenre
}

const getDeviceTradeInManufacturer = (product) => {
  return pathOr('', ['content', 'brand', 'fields', 'name'])(product)
}

const getDeviceTradeInModel = (product) => {
  return pathOr('', ['content', 'analyticsName'])(product)
}

const getSKU = (product) => {
  return product.id || product.system?.contentfulEntryId
}

const getProductName = (product) => {
  return stringFormatter(pipe(getProdName, formatString)(product), { replaceHyphens: true })
}

const getContractLength = (product) => {
  return getContractTerm(product)
}

const getProductQuantity = (product) => {
  return product.quantity
}

const getProductStatus = (product, itemsByStatus) => {
  const { backordered, outOfStock } = itemsByStatus
  if (backordered.includes(product.id)) return 'item_on_backorder'
  if (outOfStock.includes(product.id)) return 'item_unavailable'
  return undefined
}

const getProductTags = (product) => {
  return product.tags
}

export const getMobilitySim = (cart, mobilityItem) => {
  const mobilityOffers = getMobilityOffers(cart)
  let mobilityOfferSubItemIds = []

  if (mobilityItem.category === 'MOBILITY_OFFER') {
    // mobilityItem is the offer (this is the case for BYOD)
    mobilityOfferSubItemIds = mobilityItem.items
  } else {
    // retrieve offer corresponding to mobilityItem (non-BYOD)
    const mobilityOffer = mobilityOffers?.find((offer) => offer.items?.includes(mobilityItem.id))
    mobilityOfferSubItemIds = mobilityOffer?.items
  }

  // Find the MOBILITY_SIM for the offer
  const cartItemsAsValues = Object.values(cart.items)
  const mobilitySimItem = cartItemsAsValues?.find(
    (item) => item.category === 'MOBILITY_SIM' && mobilityOfferSubItemIds?.includes(item.id)
  )
  return mobilitySimItem
}

const getDeviceConfiguration = (product, cart) => {
  try {
    const byodOffers = getBYODOffers(cart)
    const matchesProduct = byodOffers?.some((offer) => offer.id === product.id)
    if (!(isMobilePhone(product) || matchesProduct)) return undefined

    const mobilitySimItem = getMobilitySim(cart, product)

    if (isEsim(mobilitySimItem)) return 'ESIM'

    return 'PSIM'
  } catch {
    return undefined
  }
}

const getProductData = (product, cart, itemsByStatus) => {
  const priceTotal = getProductPrice(product)

  return {
    sku: getSKU(product),
    contractLength: getContractLength(product),
    deviceConfiguration: getDeviceConfiguration(product, cart),
    deviceTradeInManufacturer: getDeviceTradeInManufacturer(product),
    deviceTradeInModel: getDeviceTradeInModel(product),
    offerOrderType: getOfferOrderType(product, cart),
    priceTotal,
    listPrice: priceTotal,
    productBrand: getProductBrand(product),
    productColour: getProductColour(product),
    productGenre: getProductGenre(product),
    productName: getProductName(product),
    productProvisioner: getProductProvisioner(product),
    productSize: getDeviceStorage(product),
    productStatus: getProductStatus(product, itemsByStatus),
    quantity: getProductQuantity(product),
    serviceTier: getServiceTier(product.tags),
    tags: getProductTags(product)
  }
}

const getPromotions = (cart) => {
  const trackedPromotions = [
    {
      productName: 'TELUS Online Security - Standard',
      promotionId: '9161040095713289868',
      values: {
        category: 'online_security',
        subCategory: 'not_core_product',
        productType: 'discount'
      }
    },
    {
      productName: 'Coupon Holder',
      promotionId: '9163644627335645127',
      values: {}
    }
  ]

  const promotions = Object.values(cart.items).map((item) => {
    const promotion = trackedPromotions.find(
      ({ productName }) => item?.productOffering?.name === productName
    )
    if (!promotion) return undefined

    const allAlterations = item.itemPrice.recurring.reduce((prev, curr) => {
      return prev.concat(curr.alterations)
    }, [])
    const alteration = allAlterations.find(({ promotionId }) => {
      return promotion.promotionId === promotionId
    })
    if (!alteration) return undefined

    return {
      sku: promotion.promotionId,
      name: alteration.name,
      productName: alteration.name,
      ...promotion.values
    }
  })

  return promotions.filter(Boolean)
}

const getProducts = (cart) => {
  const itemsByStatus = {
    backordered: getBackorderedItems(cart).map(({ id }) => id),
    outOfStock: getOutOfStockItems(cart).map(({ id }) => id)
  }
  const allItems = pipe(path(['items']), values)(cart)
  const mappedItems = allItems?.map((item) => getProductData(item, cart, itemsByStatus))
  const sortedItems = sortWith([
    descend(path(['_telus', 'productName'])),
    descend(prop('priceTotal'))
  ])(mappedItems)
  const groupedItems = groupWith(equals, sortedItems)

  const promotions = getPromotions(cart)
  const finalItemList = groupedItems.map((item) => {
    return mergeAll([item[0], { quantity: pipe(map(prop('quantity')), sum)(item) }])
  })
  return finalItemList.concat(promotions)
}
/* Products - End */

/* Number of products by type - Start */
const countMatchingItems = (cart, matcher) =>
  pipe(prop('items'), values, filter(matcher), length)(cart)

const getNumberOfItems = (category, cart) => {
  const hasCategory = (cartItem) => {
    return propEq('category', category)(cartItem)
  }

  return countMatchingItems(cart, hasCategory)
}

const getNumberOfAccessories = (cart) => {
  return getNumberOfItems('MOBILITY_ACCESSORY', cart)
}

const getNumberOfPhones = (cart) => {
  const hasPhoneWithoutSmartWatch = (cartItem) => {
    return allPass([
      propEq('category', 'MOBILITY_PHONE'),
      pipe(propOr('', 'tags'), includes('smartwatch'), not)
    ])(cartItem)
  }

  return countMatchingItems(cart, hasPhoneWithoutSmartWatch)
}

const getNumberOfTablets = (cart) => {
  return getNumberOfItems('MOBILITY_TABLET', cart)
}

const getNumberOfWatches = (cart) => {
  const hasSmartwatch = (cartItem) => {
    return allPass([
      propEq('category', 'MOBILITY_PHONE'),
      pipe(propOr('', 'tags'), includes('smartwatch'))
    ])(cartItem)
  }

  return countMatchingItems(cart, hasSmartwatch)
}

const getProductsByType = (cart) => ({
  numberOfAccessories: getNumberOfAccessories(cart),
  numberOfPhones: getNumberOfPhones(cart),
  numberOfTablets: getNumberOfTablets(cart),
  numberOfWatches: getNumberOfWatches(cart)
})
/* Number of products by type - End */

/* Validation errors - Start */
const getValidationErrors = (cart) => {
  return cart.root.validationErrors.map((error) => error?.code).join('|')
}
/* Validation errors - End */

/* Mobility promo code - Start */
const getMobilityPromoCode = (cart) => {
  const { promotions } = cart
  if (!promotions) return undefined

  const promotionMatch = Object.values(promotions).find((promotion) => {
    return !!promotion?.selectedCoupon
  })

  return promotionMatch?.id
}
/* Mobility promo code - End */

export {
  getOrderType,
  getProducts,
  getProductData,
  getProductName,
  getProductsByType,
  getValidationErrors,
  getMobilityPromoCode
}
